import { h, Component } from "preact";
import { Link } from "preact-router/match";
import CMSComponent from "../CMSComponent";
import { css } from "emotion";
import Image from "../Image";
import LogoServices from "../../services/LogoServices";
import ColorServices from "../../services/ColorServices";
import config from "../../config";

import { firstOfType, max } from "../../utils/CssUtils";

const dropdownIcon = require("../../assets/ic_dropdown_white.svg");
const charityIcon = require("../../assets/registered_charity_logo.png");
const footerLogo = LogoServices.getLogo("whiteTeal");
const helpDocsURL = config("HELP_DOCS_URL");

const wrapper = css({
  backgroundColor: "#4e4757",
  paddingTop: 40
});

const underBar = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderTop: "1px solid white",
  padding: "16px 32px",
  p: {
    color: "#fff",
    marginBottom: 0,
    fontSize: 12,
    marginLeft: 24,
    letterSpacing: -0.28
  },
  [max(768)]: {
    p: {
      marginLeft: 6
    }
  },
  [max(728)]: {
    flexDirection: "column",
    p: {
      order: 99,
      marginLeft: 0
    }
  }
});

const links = css({
  borderBottom: "1px solid white",
  padding: "0px 60px 50px 60px",
  display: "flex",
  color: ColorServices.getColor("white"),
  position: "relative",
  img: {
    width: 140,
    height: 24,
    marginRight: 80
  },
  div: {
    marginRight: 80,
    h2: {
      fontSize: 12,
      marginBottom: 16
    }
  },
  ul: {
    listStyleType: "none",
    margin: 0,
    padding: 0
  },
  a: {
    color: ColorServices.getColor("white"),
    fontSize: 12,
    fontWeight: 600
  },
  li: {
    marginBottom: 8,
    fontSize: 12,
    [firstOfType()]: {
      a: {
        color: "#57cfc7"
      }
    }
  },
  [max(960)]: {
    "#findEvents": {
      display: "none"
    },
    "#hostEvents": {
      display: "none"
    }
  },
  [max(728)]: {
    padding: "0px 14px 24px 14px",
    display: "block",
    margin: "0 auto",
    div: {
      marginRight: 0,
      display: "block",
      margin: "0 auto",
      marginBottom: 24,
      textAlign: "center"
    },
    img: {
      width: 140,
      height: 24,
      display: "block",
      margin: "0 auto",
      marginBottom: 24
    }
  }
});

const charityIconStyles = css({
  width: "80px !important",
  height: "80px !important",
  marginRight: "60px !important",
  position: "absolute",
  right: 0,
  bottom: 32,
  [max(728)]: {
    position: "relative",
    right: "auto",
    bottom: "auto",
    margin: "0 auto !important"
  }
});

const socialMedia = css({
  width: 135,
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row-reverse",
  [max(728)]: {
    margin: "12px 0px"
  }
});

const drowDown = css({
  fontSize: 12,
  borderRadius: 4,
  backgroundColor: ColorServices.getColor("footer"),
  borderColor: "transparent",
  backgroundImage: `url(${dropdownIcon})`,
  backgroundRepeat: "no-repeat",
  backgroundPositionX: "100%",
  backgroundPositionY: "50%",
  padding: "5px 12px",
  verticalAlign: "super",
  paddingRight: 24,
  paddingLeft: 0,
  color: "#fff",
  margin: 0,
  marginRight: 16,
  option: {
    "&:last-of-type": {
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4
    }
  },
  [max(374)]: {
    marginRight: 0
  }
});

const leftSide = css({
  display: "flex",
  color: "white",
  alignItems: "center",
  a: {
    color: "white",
    fontSize: 12,
    padding: "0px 16px",
    letterSpacing: -0.28
  },
  [max(768)]: {
    a: {
      padding: "0px 6px"
    }
  },
  [max(374)]: {
    flexDirection: "column",
    a: {
      marginTop: 12
    }
  }
});

class Footer extends Component {
  changeLocaleFunction = e => {
    const { changeLocale } = this.props;
    changeLocale(e.target.value);
  };

  render() {
    const { locale, localeData, contentData } = this.props;
    const { consoleURL } = localeData;
    const socialMediaLinks =
      contentData && contentData.data && contentData.data.socialMedia
        ? contentData.data.socialMedia
        : [];

    const hostList = [
      { text: "Create events", link: `${consoleURL}/signin` },
      { text: "Online", link: `/${locale}/online` },
      { text: "Festivals", link: `/${locale}/festivals` },
      { text: "Conferences", link: `/${locale}/conferences` },
      { text: "Education", link: `/${locale}/education` },
      { text: "Charity events", link: `/${locale}/charities` },
      { text: "Government", link: `/${locale}/government` },
      { text: "Live shows", link: `/${locale}/live-shows` }
    ];

    const useHumanitixList = [
      { text: "Features & pricing", link: `/${locale}/features-and-pricing` },
      { text: "All features", link: `/${locale}/features` },
      { text: `Sign in`, link: `${consoleURL}/signin`, useATag: true },
      { text: `Help`, link: helpDocsURL, useATag: true }
    ];

    const findEventsList = {
      au: [
        { text: "Search events", link: `/${locale}/search` },
        {
          text: "Online events",
          link: `/${locale}/events/online`
        },
        {
          text: "Adelaide events",
          link: `/${locale}/events/australia/adelaide`
        },
        {
          text: "Brisbane events",
          link: `/${locale}/events/australia/brisbane`
        },
        {
          text: "Canberra events",
          link: `/${locale}/events/australia/canberra`
        },
        {
          text: "Melbourne events",
          link: `/${locale}/events/australia/melbourne`
        },
        { text: "Perth events", link: `/${locale}/events/australia/perth` },
        {
          text: "Sydney events",
          link: `/${locale}/events/australia/sydney`
        },
        { text: "Events this weekend", link: `/${locale}/events/weekend` }
      ],
      nz: [
        { text: "Search events", link: `/${locale}/search` },
        {
          text: "Online events",
          link: `/${locale}/events/online`
        },
        {
          text: "Wellington events",
          link: `/${locale}/events/new-zealand/wellington`
        },
        {
          text: "Tauranga events",
          link: `/${locale}/events/new-zealand/tauranga`
        },
        { text: "Events this weekend", link: `/${locale}/events/weekend` }
      ],
      us: []
    };

    const aboutList = [
      { text: "Contact us", link: `/${locale}/contact-us` },
      { text: `Our impact`, link: `/${locale}/our-impact` },
      { text: `Our model`, link: `/${locale}/our-model` },
      { text: `Our story`, link: `/${locale}/our-story` },
      { text: `Our team`, link: `/${locale}/our-team` },
      { text: "Accessible events", link: `/${locale}/accessible-events` },
      {
        text: "Partners and supporters",
        link: `/${locale}/partners-and-supporters`
      }
      // { text: "Press", link: `/${locale}/press` }
    ];

    return (
      <footer className={wrapper}>
        <div class={links}>
          <a href={`/${locale}/`}>
            <img src={footerLogo} alt="Humanitix Logo" />
          </a>
          {findEventsList[locale].length ? (
            <div id="findEvents">
              <h2>FIND EVENTS</h2>
              <ul>
                {findEventsList[locale].map((item, index) => {
                  return (
                    <li id={`FIND_EVENTS_${index}`}>
                      <Link href={item.link}>{item.text}</Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
          <div id="hostEvents">
            <h2>HOST EVENTS</h2>
            <ul>
              {hostList.map((item, index) => {
                return (
                  <li id={`HOST_EVENTS_${index}`}>
                    {item.useATag ? (
                      <a href={item.link} aria-label={item.text}>
                        {item.text}
                      </a>
                    ) : (
                      <Link href={item.link}>{item.text}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <h2>USE HUMANITIX</h2>
            <ul>
              {useHumanitixList.map((item, index) => {
                return (
                  <li id={`USE_HUMANITIX_${index}`}>
                    {item.useATag ? (
                      <a href={item.link} aria-label={item.text}>
                        {item.text}
                      </a>
                    ) : (
                      <Link href={item.link}>{item.text}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <h2>ABOUT HUMANITIX</h2>
            <ul>
              {aboutList.map((services, index) => {
                return (
                  <li id={`ABOUT_HUMANITIX_${index}`}>
                    <Link href={services.link}>{services.text}</Link>
                  </li>
                );
              })}
            </ul>
          </div>
          {locale === "au" ? (
            <Image
              imageURL={charityIcon}
              styleSheet={charityIconStyles}
              alt="Australian Approved Charity"
            />
          ) : null}
        </div>

        <div className={underBar}>
          <div className={leftSide}>
            <select
              onChange={this.changeLocaleFunction}
              className={drowDown}
              label="Select your locale"
              aria-label="Select your locale"
            >
              <option
                value="au"
                selected={locale === "au"}
                label="Australia"
                aria-label="Australia"
              >
                Australia
              </option>
              <option
                value="nz"
                selected={locale === "nz"}
                label="New Zealand"
                aria-label="New Zealand"
              >
                New Zealand
              </option>
              <option
                value="us"
                selected={locale === "us"}
                label="United States"
                aria-label="United States"
              >
                United states
              </option>
            </select>

            <a href={`${consoleURL}/public/pdfs/humanitix_privacy_policy.pdf`}>
              Privacy Policy
            </a>
            <a
              href={`${consoleURL}/public/pdfs/humanitix_promoter_agreement-${locale}.pdf`}
            >
              Terms of use
            </a>
          </div>
          <p>
            {locale === "nz"
              ? `© Humanitix New Zealand Charitable Trust ${new Date().getFullYear()}`
              : `© Humanitix Limited ${new Date().getFullYear()}`}
          </p>
          <div className={socialMedia}>
            {socialMediaLinks.links
              ? socialMediaLinks.links.map((link, index) => {
                  return (
                    <a
                      href={link}
                      aria-label={socialMediaLinks.ariaLabels[index]}
                    >
                      <Image
                        imageURL={socialMediaLinks.icons[index].fields.file.url}
                        alt={socialMediaLinks.icons[index].fields.description}
                      />
                    </a>
                  );
                })
              : null}
          </div>
        </div>
      </footer>
    );
  }
}

export default CMSComponent("footer", Footer, 470);
